// Enhanced authenticated fetch with JWT token from _aristai_id cookie
// Includes both HttpOnly cookies and JWT token in Authorization header
import { refreshAuthToken, shouldAttemptRefresh } from '../services/tokenRefreshService';

// Track if we're currently refreshing to prevent duplicate refresh attempts
let isCurrentlyRefreshing = false;
let refreshPromise = null;

/**
 * Extract JWT token from _aristai_id cookie
 */
const getJWTToken = () => {
  try {
    // For localhost development, use environment variable token
    if (window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1') {
      // Get token from environment variable
      const devToken = process.env.REACT_APP_DEV_TOKEN;
      if (devToken && devToken !== 'PASTE_YOUR_COPIED_TOKEN_HERE') {
        // Also set the cookie for backend compatibility
        document.cookie = `_aristai_id=${devToken}; path=/`;
        return devToken;
      }
    }
    
    // Get all cookies
    const cookies = document.cookie.split(';');
    
    // Find the _aristai_id cookie
    const aristaiIdCookie = cookies.find(cookie => 
      cookie.trim().startsWith('_aristai_id=')
    );
    
    if (aristaiIdCookie) {
      // Extract the token value (everything after '=')
      const token = aristaiIdCookie.split('=')[1];
      return token || null;
    }
    
    return null;
  } catch (error) {
    console.warn('Error extracting JWT token from cookie:', error);
    return null;
  }
};

/**
 * Make an authenticated API request with automatic JWT token handling
 * Includes both HttpOnly cookies and JWT token in Authorization header
 */
export const authenticatedFetch = async (url, options = {}) => {
  const isDevMode = process.env.REACT_APP_DEV_MODE === 'true';
  
  if (isDevMode) {
    console.log('🔄 Making authenticated request to:', url);
  }
  
  // Get JWT token from _aristai_id cookie
  const jwtToken = getJWTToken();
  
  // Prepare headers with JWT token
  const headers = {
    'Content-Type': 'application/json',
    ...options.headers
  };

  // Add JWT token to Authorization header if available
  if (jwtToken) {
    headers['Authorization'] = `Bearer ${jwtToken}`;
    if (isDevMode) {
      console.log('🔑 JWT token added to Authorization header');
    }
  } else {
    if (isDevMode) {
      console.warn('⚠️ No JWT token found in _aristai_id cookie');
    }
  }

  // Make the request with both credentials and JWT token
  const response = await fetch(url, {
    ...options,
    headers,
    credentials: 'include' // This sends HttpOnly cookies automatically
  });

  // Handle 401 responses (not authenticated)
  if (response.status === 401) {
    if (isDevMode) {
      console.warn('⚠️ 401 response received, attempting token refresh...');
    }
    
    // Check if we should attempt refresh
    if (shouldAttemptRefresh()) {
      // If already refreshing, wait for it
      if (isCurrentlyRefreshing && refreshPromise) {
        if (isDevMode) {
          console.log('⏳ Waiting for ongoing token refresh...');
        }
        const refreshSuccess = await refreshPromise;
        
        if (refreshSuccess) {
          // Get the new JWT token after refresh
          const newJwtToken = getJWTToken();
          
          // Update headers with new token
          const retryHeaders = {
            ...headers
          };
          
          if (newJwtToken) {
            retryHeaders['Authorization'] = `Bearer ${newJwtToken}`;
          }
          
          // Retry the original request
          const retryResponse = await fetch(url, {
            ...options,
            headers: retryHeaders,
            credentials: 'include'
          });
          
          return retryResponse;
        }
      } else {
        // Start new refresh
        isCurrentlyRefreshing = true;
        refreshPromise = refreshAuthToken();
        
        try {
          const refreshSuccess = await refreshPromise;
          
          if (refreshSuccess) {
            if (isDevMode) {
              console.log('✅ Token refreshed successfully, retrying original request...');
            }
            
            // Get the new JWT token after refresh
            const newJwtToken = getJWTToken();
            
            // Update headers with new token
            const retryHeaders = {
              ...headers
            };
            
            if (newJwtToken) {
              retryHeaders['Authorization'] = `Bearer ${newJwtToken}`;
            }
            
            // Retry the original request with updated token
            const retryResponse = await fetch(url, {
              ...options,
              headers: retryHeaders,
              credentials: 'include'
            });
            
            // If retry also fails with 401, don't attempt another refresh
            if (retryResponse.status === 401) {
              if (isDevMode) {
                console.error('❌ Request failed again after token refresh');
              }
              // Fall through to redirect logic below
            } else {
              // Successful retry
              return retryResponse;
            }
          } else {
            if (isDevMode) {
              console.warn('⚠️ Token refresh failed');
            }
          }
        } finally {
          isCurrentlyRefreshing = false;
          refreshPromise = null;
        }
      }
    }
    
    // If in dev mode and refresh failed, still return the response for testing
    if (isDevMode) {
      console.warn('⚠️ DEV MODE: Returning 401 response for testing');
      return response;
    }
    
    // In production, redirect to login if refresh failed or was not attempted
    console.warn('❌ Authentication failed, redirecting to login');
    const currentUrl = window.location.href;
    const authUrl = process.env.REACT_APP_AUTH_URL || 'https://login.aristai.io';
    const loginUrl = `${authUrl}/auth/signin?to=${encodeURIComponent(currentUrl)}`;
    window.location.href = loginUrl;
    throw new Error('Authentication failed');
  }

  if (isDevMode) {
    console.log('✅ Authenticated request successful:', response.status);
  }
  return response;
};

/**
 * Helper function for common API patterns
 */
export const apiRequest = {
  get: (url, options = {}) => authenticatedFetch(url, { method: 'GET', ...options }),
  
  post: (url, data, options = {}) => authenticatedFetch(url, {
    method: 'POST',
    body: JSON.stringify(data),
    ...options
  }),
  
  put: (url, data, options = {}) => authenticatedFetch(url, {
    method: 'PUT',
    body: JSON.stringify(data),
    ...options
  }),
  
  delete: (url, options = {}) => authenticatedFetch(url, { method: 'DELETE', ...options })
};

/**
 * Get current user info from token (helper function)
 */
export const getCurrentUser = () => {
  // OPTION C: No client-side user extraction needed
  // User info comes from the auth context via API calls
  if (process.env.REACT_APP_DEV_MODE === 'true') {
    console.warn('getCurrentUser() is deprecated in Option C - use AuthContext instead');
  }
  return null;
};